import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import config from "../../config";


export default function AddAdmin() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    accessLevel: "Admin",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("success");

  const showDialog = (title,message,type = "success") => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (
        !formData.name.trim() ||
        !formData.email.trim() ||
        !formData.password.trim()
      ) {
        showDialog(
          "Validation Error",
          "Please fill all fields",
          "error"
        );
        return;
      }

      setLoading(true);

      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `${config.apiBaseUrl}/admin/auth/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email.toLowerCase(),
            password: formData.password,
            accessLevel: formData.accessLevel,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
       showDialog(
          "Success",
          "Admin created successfully",
          "success"
        );

        setFormData({
          name: "",
          email: "",
          accessLevel: "Admin",
          password: "",
        });

        showDialog(
          "Success",
          "Admin created successfully",
          "success"
        );
      } else {
       showDialog(
          "Error",
          data.message || "Failed to create admin",
          "error"
        );
      }
    } catch (error) {
      showDialog(
        "Error",
        "Something went wrong",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: "#F8FAFC",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
              md: 4,
            },
          }}
        >
          {isMobile ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={3}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Typography
                variant="h6"
                fontWeight={800}
              >
                Add Admin
              </Typography>
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              mb={4}
            >
              <IconButton
                onClick={() => navigate("/admins")}
              >
                <ArrowBackRoundedIcon />
              </IconButton>

              <Box>
                <Typography
                  variant="h4"
                  fontWeight={800}
                >
                  Add Administrator
                </Typography>

                <Typography
                  color="text.secondary"
                  mt={0.5}
                >
                  Create a new administrator account
                </Typography>
              </Box>
            </Stack>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  boxShadow:
                    "0 10px 30px rgba(0,0,0,0.05)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  mb={4}
                >
                  <AdminPanelSettingsRoundedIcon
                    color="primary"
                  />

                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    Administrator Details
                  </Typography>
                </Stack>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Role"
                      name="accessLevel"
                      value={formData.accessLevel}
                      onChange={handleChange}
                    >
                      <MenuItem value="Admin">
                        Admin
                      </MenuItem>

                      <MenuItem value="SuperAdmin">
                        Super Admin
                      </MenuItem>
                      <MenuItem value="Standard">
                        Standard 
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      type={
                        showPassword
                          ? "text"
                          : "password"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowPassword(
                                  !showPassword
                                )
                              }
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 5,
                    pt: 3,
                    borderTop:
                      "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    justifyContent="flex-end"
                    spacing={2}
                  >
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate("/admins")
                      }
                      sx={{
                        minWidth: 120,
                        borderRadius: 3,
                        textTransform: "none",
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      sx={{
                        minWidth: 180,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",

                        "&:hover": {
                          background:
                            "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                        },
                      }}
                    >
                      {loading ? "Creating..." : "Create Admin"}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  height: "100%",
                  background:
                    "linear-gradient(180deg,#EFF6FF 0%,#FFFFFF 100%)",
                  border:
                    "1px solid rgba(37,99,235,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  mb={3}
                >
                  Role Permissions
                </Typography>

                <Box mb={4}>
                  <Typography
                    fontWeight={700}
                    color="primary"
                    mb={1}
                  >
                    Standard Admin
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Can manage recordings, surveys,
                    reports and general administrative
                    tasks.
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    fontWeight={700}
                    color="error"
                    mb={1}
                  >
                    Super Admin
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Full system access including user
                    management, permissions, settings,
                    recordings, surveys and reports.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            color:
              dialogType === "success"
                ? "success.main"
                : "error.main",
            fontWeight: 700,
          }}
        >
          {dialogTitle}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setDialogOpen(false);

              if (dialogType === "success") {
                navigate("/admins");
              }
            }}
            color={
              dialogType === "success"
                ? "success"
                : "error"
            }
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}